// React&NextJS
import React, { useState } from 'react'
import { GetServerSideProps } from 'next'
// 3rd party libraries
import algoliasearch from 'algoliasearch'
import {
    Configure,
    InstantSearch,
    connectHits,
    connectPagination,
} from 'react-instantsearch-dom'
import { EmailIcon, EmailShareButton } from 'react-share'
// Services&Helper functions
import helperFunctions from '../utils/helperFunctions'
// Types
import { MemoryRefType, ScrapedFunerals } from '../types/types'
// Styles
import s from '../styles/pages/RecentFunerals.module.scss'
import c from 'classnames'
// Components
import { Button } from '../components/core/button/Button'
import { Container } from '../components/core/container/Container'
import FuneralCards from '../components/components/funeralCards/FuneralCards'
import { GalleryWithTimer } from '../components/components/galleryWithTimer/GalleryWithTimer'
import Meta from '../components/meta/Meta'
import HOCModal from '../components/components/modals/HOCModal'
import { Section } from '../components/core/section/Section'
import { ServiceCard } from '../slices'
import { Text } from '../components/core/text/Text'

// algolia config
const client = algoliasearch(
    process.env.NEXT_PUBLIC_ALGOLIA_APPLICATION_ID,
    process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_API_KEY
)

const index = client.initIndex('memories')

type HitType = MemoryRefType & {
    objectID: string
}

type HitsType = {
    hits: Array<HitType>
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
    setFuneral: React.Dispatch<React.SetStateAction<ScrapedFunerals>>
}

export const getServerSideProps: GetServerSideProps = async ({ res }) => {
    res.setHeader(
        'Cache-Control',
        'public, s-maxage=600, stale-while-revalidate=300'
    )
    let memHits = {}

    // Get all the memories hits from firebase with algolia
    try {
        memHits = await index.search('', {
            filters: 'NOT data.scraped=1',
        })
    } catch (e) {
        console.error((e as Error).message)
    }

    return {
        props: {
            memHits,
        },
    }
}

function Hits({ hits }: HitsType) {
    const memories = hits.map((h: any) => {
        return { ...h.data, objectID: h.objectID }
    })
    const [todayList, tomorrowList, upcomingList] =
        helperFunctions.orderFunerals(memories)

    const [isOpen, setIsOpen] = useState(false)
    const [funeral, setFuneral] = useState<ScrapedFunerals>(null)

    const closeModal = () => {
        setFuneral(null)
        setIsOpen(false)
    }
    return (
        <>
            <HOCModal
                isOpen={isOpen}
                closeModal={() => closeModal()}
                title={
                    'Viltu stofna eða benda aðstandanda á að stofna minningarsíðu?'
                }
                info={`Við sjáum að ekki er til minningarsíða um ${funeral?.fullName} á Minningar.is. Þú getur stofnað minningarsíðu í nokkrum einföldum skrefum, eða bent aðstandanda á það.`}
                data={funeral}
                imgURL="https://firebasestorage.googleapis.com/v0/b/minning-f44b2.appspot.com/o/Fallback%2FiStock-526494097-min.jpeg?alt=media&token=e574b1a7-7266-4463-a2a7-f9d54609901c"
            >
                <Button
                    type="button"
                    variant="ghost"
                    className={s.btnPadd}
                    to={`/memories/form?funeralId=${funeral?.objectID}`}
                >
                    Stofna minningarsíðu
                </Button>
                <Text variant="date">Senda hugmyndina á aðstandanda</Text>
                <div className={s.icons}>
                    <EmailShareButton
                        url={`https://minningar.is/memories/form?funeralId=${funeral?.objectID}`}
                        subject={`Þekkir þú ${funeral?.fullName}?`}
                        body={`Ekki hefur verið skrifuð minningarsíða um ${funeral?.fullName}. Smelltu á linkinn til að stofna minningarsíðu um ${funeral?.fullName} í nokkrum einföldum skrefum:`}
                        separator={'\n'}
                    >
                        <EmailIcon
                            size={48}
                            round={true}
                            onClick={() => closeModal()}
                        />
                    </EmailShareButton>
                </div>
            </HOCModal>
            {todayList.length > 0 && (
                <Container className={s.subHeader}>
                    <Text variant="h5" className={s.subHeader__item}>
                        Í dag
                    </Text>
                    <FuneralCards
                        cards={todayList}
                        info="í dag"
                        openModal={() => setIsOpen((prev) => !prev)}
                        setFuneral={setFuneral}
                    />
                </Container>
            )}
            {tomorrowList.length > 0 && (
                <Container className={s.subHeader}>
                    <Text variant="h5" className={s.subHeader__item}>
                        Á morgun
                    </Text>
                    <FuneralCards
                        cards={tomorrowList}
                        info="á morgun"
                        openModal={() => setIsOpen((prev) => !prev)}
                        setFuneral={setFuneral}
                    />
                </Container>
            )}
            {upcomingList.length > 0 && (
                <Container className={s.subHeader}>
                    <Text variant="h5" className={s.subHeader__item}>
                        Á næstunni
                    </Text>
                    <FuneralCards
                        cards={upcomingList}
                        info="á næstunni"
                        openModal={() => setIsOpen((prev) => !prev)}
                        setFuneral={setFuneral}
                    />
                </Container>
            )}
        </>
    )
}

const Pagination = ({ currentRefinement, nbPages, refine }) => {
    const pages = []

    for (let i = 1; i <= nbPages; i++) {
        pages.push(i)
    }

    const changePage = (event, newVal: number) => {
        event.preventDefault()
        refine(newVal)
        if (window.scrollY > 500) {
            window.scrollTo({
                top: 0,
                behavior: 'smooth',
            })
        }
    }

    const renderPages = (pages: Array<number>) => {
        let counterLeft = 0
        let counterRight = 0
        return pages.map((p) => {
            if (currentRefinement - 1 <= p && p <= currentRefinement + 1) {
                return (
                    <div
                        key={p}
                        className={c(
                            s.page,
                            p === currentRefinement && s.selected
                        )}
                    >
                        <button
                            type="button"
                            disabled={currentRefinement === p}
                            onClick={(event) => changePage(event, p)}
                        >
                            <Text variant="p">{p}</Text>
                        </button>
                    </div>
                )
            } else if (p < currentRefinement - 1 && counterLeft === 0) {
                counterLeft += 1
                return (
                    <div
                        key={p}
                        className={c(
                            s.page,
                            p === currentRefinement && s.selected
                        )}
                    >
                        <Text variant="p">...</Text>
                    </div>
                )
            } else if (p > currentRefinement + 1 && counterRight === 0) {
                counterRight += 1
                return (
                    <div
                        key={p}
                        className={c(
                            s.page,
                            p === currentRefinement && s.selected
                        )}
                    >
                        <Text variant="p">...</Text>
                    </div>
                )
            }
        })
    }

    if (nbPages > 1) {
        const first = pages.shift()
        const last = pages.pop()
        return (
            <Container>
                <div className={s.pagination}>
                    <Button
                        variant="icon"
                        icon="crumbArrow"
                        arrowDirection="l"
                        className={s.leftBtn}
                        disabled={currentRefinement === 1}
                        onClick={(event) =>
                            changePage(event, currentRefinement - 1)
                        }
                    />
                    <div className={s.pages}>
                        <div
                            className={c(
                                s.page,
                                first === currentRefinement && s.selected
                            )}
                        >
                            <button
                                type="button"
                                disabled={currentRefinement === first}
                                onClick={(event) => changePage(event, first)}
                            >
                                <Text variant="p">{first}</Text>
                            </button>
                        </div>
                        {renderPages(pages)}
                        <div
                            className={c(
                                s.page,
                                last === currentRefinement && s.selected
                            )}
                        >
                            <button
                                type="button"
                                disabled={currentRefinement === last}
                                onClick={(event) => changePage(event, last)}
                            >
                                <Text variant="p">{last}</Text>
                            </button>
                        </div>
                    </div>

                    <Button
                        variant="icon"
                        icon="crumbArrow"
                        disabled={currentRefinement === nbPages}
                        onClick={(event: React.SyntheticEvent) =>
                            changePage(event, currentRefinement + 1)
                        }
                    />
                </div>
            </Container>
        )
    } else return <></>
}

export default function RecentFunerals({ memHits }) {
    const CustomHits = connectHits(Hits)
    const today = new Date()
    const dayFilter = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate() - 1,
        23,
        59,
        59,
        59
    ).getTime()

    const CustomPagination = connectPagination(Pagination)

    return (
        <Section noDynamicPadding>
            <Meta
                title="Minningar - Útfarir"
                seoTitle="Minningar - Útfarir"
                url="https://minningar.is/recent-funerals/"
                type="website"
                description="Væntanlegar útfarir sem birtar hafa verið á minningar.is."
                image="https://firebasestorage.googleapis.com/v0/b/minning-f44b2.appspot.com/o/meta%2FMinningar_7V4A5419_01-min-nyleg-andlat.jpg?alt=media&token=54f19520-553c-40bc-9650-93fdeb36e656"
            />
            <Text variant="h1" align="center" className={s.header}>
                Útfarir á næstunni
            </Text>
            <InstantSearch searchClient={client} indexName="memories_funeral">
                <Configure
                    hitsPerPage={50}
                    filters={`data.announcements.funeral.funeralDate > ${dayFilter} AND data.announcements.funeral.hold:NOW`}
                />
                <CustomHits />
                <CustomPagination />
            </InstantSearch>
            <ServiceCard
                slice={{
                    primary: {
                        title: 'Tilkynna um útför og stofna minningarsíðu',
                        description:
                            'Það er einfalt og gjaldfrjálst að tilkynna útför á Minningar.is en við það birtist andlátstilkynning á vefnum undir „Nýleg andlát.“ Hægt er að linka á streymi frá útför í útfarartilkynningu og deila sálmaskrá fyrir þá sem ekki sjá sér fært að mæta í jarðarförina. Einnig er hægt að deila andlátstilkynningunni sem fallegu spjaldi á samfélagsmiðlum sem vísar beint inn á minningarsíðu viðkomandi.',
                        buttonLink: '/memories/form',
                        buttonText: 'Stofna minningarsíðu',
                    },
                }}
            />
            <GalleryWithTimer hits={memHits.hits} />
        </Section>
    )
}
